import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { InvoicingDocumentResponse, SearchInvoicingDocumentsRequest, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createInvoicingDocumentColumns,
  defaultInvoicingDocumentColumnVisibilityModel,
  InvoicingDocumentFormDialog,
  InvoicingDocumentDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useInvoicingDocuments,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const InvoicingDocumentsPage: React.FC = () => {
  const { can } = usePermissions();
  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchInvoicingDocumentsRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchInvoicingDocumentsRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('invoicing-document')?.query as SearchInvoicingDocumentsRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchInvoicingDocumentsRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);
  
  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultInvoicingDocumentColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<InvoicingDocumentResponse>('invoicing-document');

  // Use custom hooks for invoicing-document management
  const {
    invoicingDocuments,
    loading,
    selectedInvoicingDocument,
    setSelectedInvoicingDocument,
    fetchInvoicingDocuments,
    createInvoicingDocument,
    deleteInvoicingDocument,
    total,
  } = useInvoicingDocuments();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedInvoicingDocumentForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<InvoicingDocumentResponse>({ 
    data: invoicingDocuments, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchInvoicingDocuments(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleDeleteClick = (invoicingDocument: InvoicingDocumentResponse) => {
    setSelectedInvoicingDocument(invoicingDocument);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchInvoicingDocuments(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    const searchBody = searchData as SearchInvoicingDocumentsRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchInvoicingDocuments(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('invoicing-document')?.query as SearchInvoicingDocumentsRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchInvoicingDocuments(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: invoicingDocuments,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('invoicing_documents'),
      separator,
    });
  };

  const handleCreateInvoicingDocument = async (data: { memberIdentifier: string; invoicingDocumentJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      const { memberIdentifier, invoicingDocumentJson, aclAuthorizations } = data;

      // Validate required fields
      if (!memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!invoicingDocumentJson?.trim()) {
        throw new Error('InvoicingDocument JSON is required');
      }

      // Parse and validate JSON
      let invoicingDocumentData;
      try {
        invoicingDocumentData = parseJsonWithDates(invoicingDocumentJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!invoicingDocumentData || typeof invoicingDocumentData !== 'object') {
        throw new Error('InvoicingDocument data must be a valid JSON object');
      }

      await createInvoicingDocument(invoicingDocumentData, memberIdentifier, aclAuthorizations);
      setCreateDialogOpen(false);
      // Refresh the list after successful creation
      fetchInvoicingDocuments(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      // Error is handled by the hook, but we can add additional client-side validation errors
      console.error('InvoicingDocument creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteInvoicingDocument = async () => {
    if (!selectedInvoicingDocument?.id) return;

    await deleteInvoicingDocument(selectedInvoicingDocument.id);
    setSelectedInvoicingDocument(null);
    // Refresh the list after successful deletion
    fetchInvoicingDocuments(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  // Create columns configuration
  const columns = createInvoicingDocumentColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchInvoicingDocuments(0, 10, initialSearchQuery);
  }, [fetchInvoicingDocuments, initialSearchQuery]);

  return (
    <PageLayout 
      title="InvoicingDocuments Management"
      selectedItemInfo={selectedInvoicingDocumentForDetail ? `Selected: ${selectedInvoicingDocumentForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh invoicing_documents list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create InvoicingDocument
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search InvoicingDocuments"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="invoicing-document"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedInvoicingDocumentForDetail ? (
        <JsonDetailPanel
          title="InvoicingDocument Details"
          data={selectedInvoicingDocumentForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={invoicingDocuments}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultInvoicingDocumentColumnVisibilityModel}
          columnOrderStorageKey="invoicing_documents-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create InvoicingDocument Dialog */}
      <InvoicingDocumentFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateInvoicingDocument}
      />

      {/* Delete Confirmation Dialog */}
      <InvoicingDocumentDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteInvoicingDocument}
        invoicingDocument={selectedInvoicingDocument}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export InvoicingDocuments to CSV"
        entityName="invoicing_documents"
        defaultFilename={generateCSVFilename('invoicing_documents')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />
    </PageLayout>
  );
};

export default InvoicingDocumentsPage;